import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { MessagesSquare, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminFeedback() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-feedback"],
    queryFn: async () => (await api.get("/admin/feedback")).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.items || q.data?.feedback || [];

  const del = async (id) => {
    try {
      await api.delete(`/admin/feedback/${id}`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async () => {
    try {
      const res = await api.get("/admin/feedback/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "feedback.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.admin")}
        title={t("nav.feedback")}
        subtitle={t("admin.subtitle")}
        actions={<button onClick={download} data-testid="feedback-export" className="rounded-full bg-white border border-border h-11 px-4 text-sm hover:shadow-card">{t("admin.exportCsv")}</button>}
      />
      {q.isLoading && <LoadingCard height="h-40" />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={MessagesSquare} title={t("common.empty")} testId="feedback-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-feedback">
        {items.map((f, i) => (
          <div key={f.id || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-ink-900 text-sm whitespace-pre-wrap">{f.text || f.message || f.content || "—"}</div>
                <div className="text-xs text-ink-500 mt-2">{f.user_email || f.email || ""} · {f.created_at && new Date(f.created_at).toLocaleString()}</div>
              </div>
              {f.id && (
                <button onClick={() => del(f.id)} className="text-ink-500 hover:text-rust-600" data-testid={`feedback-del-${i}`} aria-label="delete">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
